import React, { useEffect, useState } from "react";
import { Shield, Filter, RefreshCw } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter.trim()) params.action = actionFilter.trim();
      if (entityFilter.trim()) params.entity_type = entityFilter.trim();
      params.limit = 200;
      setItems(await v2Api.audit(params) || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" /> Audit Log
        </h1>
        <p className="text-sm text-slate-500">
          Append-only record of who did what when — required for govt-buyer
          compliance audits (CAG / CVC).
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <Input value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
          placeholder="action prefix (e.g. project.create)" className="max-w-xs h-8 text-sm" />
        <Input value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
          placeholder="entity type" className="max-w-xs h-8 text-sm" />
        <Button onClick={refresh} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Apply
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-400 italic">
          No audit entries match. Actions flow here as users edit projects,
          assign approvals, etc.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 border-b bg-slate-50">
            <div className="col-span-3">When</div>
            <div className="col-span-3">Actor</div>
            <div className="col-span-2">Action</div>
            <div className="col-span-4">Summary</div>
          </div>
          {items.map((row) => (
            <div key={row.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b last:border-b-0 text-sm hover:bg-slate-50">
              <div className="col-span-3 font-mono text-[11px] text-slate-500">
                {row.created_at?.slice(0, 19).replace("T", " ")}
              </div>
              <div className="col-span-3 text-slate-700 truncate">{row.actor_email || "system"}</div>
              <div className="col-span-2 text-blue-700 font-medium text-[11px]">{row.action}</div>
              <div className="col-span-4 text-slate-600 truncate" title={JSON.stringify(row.detail)}>
                {row.summary || (row.entity_type ? `${row.entity_type}:${row.entity_id || ""}` : "—")}
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
